import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Users, Banknote, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { API, formatError } from "@/lib/api";
import { WEEKDAYS, fmtCHF, fmtDate, hexToHsl, todayZurich } from "@/lib/format";
import { priceSummary } from "@/components/dialogs/BookingDialog";
import { Field, Spinner } from "@/components/Field";

const pub = axios.create({ baseURL: `${API}/public/booking` });
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (s, n) => { const d = new Date(`${s}T00:00`); d.setDate(d.getDate() + n); return iso(d); };
const wd = (s) => (new Date(`${s}T00:00`).getDay() + 6) % 7;

const dayState = (d, room, busy, today) => {
  if (d < today) return "past";
  if (room.closed_weekdays?.includes(wd(d))) return "closed";
  const hits = busy.filter((b) => b.start.slice(0, 10) <= d && b.end.slice(0, 10) >= d);
  if (hits.some((b) => b.state === "occupato")) return "busy";
  return hits.length ? "pending" : "free";
};
const LABEL = { past: "", closed: "chiusa", busy: "occupata", pending: "richiesta", free: "libera" };

const DayStrip = ({ start, setStart, days, busy, room, from, to, onSelect, today }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Button type="button" variant="outline" size="icon" className="h-10 w-10" data-testid="public-prev-days" disabled={start <= today} onClick={() => setStart(addDays(start, -14))}><ChevronLeft className="h-4 w-4" /></Button>
      <span className="text-sm font-semibold">{fmtDate(days[0])} – {fmtDate(days[days.length - 1])}</span>
      <Button type="button" variant="outline" size="icon" className="h-10 w-10" data-testid="public-next-days" onClick={() => setStart(addDays(start, 14))}><ChevronRight className="h-4 w-4" /></Button>
    </div>
    <div className="grid grid-cols-7 gap-1.5">
      {days.map((d) => {
        const st = dayState(d, room, busy, today);
        const off = ["past", "closed", "busy"].includes(st);
        const sel = from && d >= from && d <= (to || from);
        return (
          <button type="button" key={d} disabled={off} data-testid={`public-day-${d}`} onClick={() => onSelect(d)}
            className={`rounded-lg border p-2 text-center text-sm ${sel ? "border-primary bg-primary text-white" : "border-slate-200"} ${off ? "bg-slate-100 text-slate-400 cursor-not-allowed" : sel ? "" : "bg-white hover:border-primary"}`}>
            <div className="text-xs">{WEEKDAYS[wd(d)].slice(0, 3)}</div>
            <div className="font-semibold">{Number(d.slice(8))}</div>
            <div className={`text-[10px] ${sel ? "text-white" : st === "free" ? "text-green-700" : st === "pending" ? "text-amber-700" : ""}`}>{LABEL[st]}</div>
          </button>
        );
      })}
    </div>
  </div>
);

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [roomId, setRoomId] = useState("");
  const [start, setStart] = useState(todayZurich());
  const [avail, setAvail] = useState(null);
  const [f, setF] = useState({ date_from: "", date_to: "", cleaning: false, first_name: "", last_name: "", email: "", phone: "", event_type: "", participants: "", notes: "", accept_rules: false, accept_privacy: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const days = useMemo(() => Array.from({ length: 14 }, (_, i) => addDays(start, i)), [start]);
  const room = info?.rooms.find((r) => r.id === roomId);

  useEffect(() => {
    pub.get(`/${slug}`).then((r) => {
      setInfo(r.data);
      if (r.data.rooms.length === 1) setRoomId(r.data.rooms[0].id);
      if (r.data.primary_color) document.documentElement.style.setProperty("--primary", hexToHsl(r.data.primary_color));
    }).catch((e) => setError(formatError(e)));
  }, [slug]);

  useEffect(() => {
    if (!roomId) return;
    pub.get(`/${slug}/availability`, { params: { room_id: roomId, date_from: days[0], date_to: days[13] } }).then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, [slug, roomId, days]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await pub.post(`/${slug}/requests`, { ...f, room_id: roomId, cleaning: !!(f.cleaning && room.cleaning_enabled), participants: Number(f.participants), rules_version: room.rules_version || 0, notes: f.notes || null });
      setDone(true);
    } catch (err) { setMsg(formatError(err)); } finally { setBusy(false); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center p-6 text-slate-600" data-testid="public-unavailable">{error}</div>;
  if (!info) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;
  const today = avail?.today || todayZurich();
  const pick = (d) => setF((s) => {
    if (!s.date_from || s.date_to !== s.date_from || d < s.date_from) return { ...s, date_from: d, date_to: d };
    for (let x = s.date_from; x <= d; x = addDays(x, 1)) {
      if (["past", "closed", "busy"].includes(dayState(x, room, avail?.busy || [], today))) return { ...s, date_from: d, date_to: d };
    }
    return { ...s, date_to: d };
  });
  const sum = priceSummary(room, f.date_from, f.date_to, f.cleaning);

  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-700">
      <header className="bg-white border-b"><div className="max-w-3xl mx-auto flex items-center gap-3 px-4 h-20">
        {info.has_logo && <img src={`${API}/public/booking/${slug}/logo`} alt="" className="h-12 w-12 object-contain" />}
        <div><div className="font-heading font-bold text-lg text-slate-900">{info.name}</div><div className="text-sm text-slate-500">Richiesta di prenotazione sala</div></div>
      </div></header>
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {done ? (
          <div data-testid="public-success" className="rounded-2xl border border-green-200 bg-white p-8 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-green-700 mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Richiesta inviata</h1>
            <p>La tua richiesta <strong>non è ancora confermata</strong>. L'associazione la esaminerà e ti risponderà all'indirizzo {f.email}.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <section className="space-y-3">
              <h2 className="font-heading text-lg font-semibold text-slate-900">1. Scegli la sala</h2>
              {info.rooms.length === 0 && <p data-testid="public-no-rooms">Al momento non ci sono sale prenotabili.</p>}
              <div className="grid sm:grid-cols-2 gap-3 items-start">
                {info.rooms.map((r) => (
                  <button type="button" key={r.id} data-testid={`public-room-${r.id}`} onClick={() => { setRoomId(r.id); setF((s) => ({ ...s, date_from: "", date_to: "" })); }} className={`text-left rounded-xl border bg-white overflow-hidden ${roomId === r.id ? "border-primary ring-2 ring-primary" : "border-slate-200"}`}>
                    {r.has_photo && <img src={`${API}/public/booking/${slug}/rooms/${r.id}/photo?v=${r.photo_version || 0}`} alt={`Foto ${r.name}`} data-testid={`public-room-photo-${r.id}`} className="h-40 w-full object-cover" />}
                    <div className="p-4">
                    <div className="font-semibold text-slate-900">{r.name}</div>
                    {r.description && <div className="text-sm text-slate-500">{r.description}</div>}
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-1">
                      {r.capacity && <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{r.capacity}</span>}
                      {r.price_per_day != null && <span className="inline-flex items-center gap-1 font-semibold text-slate-700"><Banknote className="h-3.5 w-3.5" />{fmtCHF(r.price_per_day)} / giorno</span>}
                      {r.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{r.address}</span>}
                    </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
            {room && avail && (
              <section className="space-y-3">
                <h2 className="font-heading text-lg font-semibold text-slate-900">2. Scegli i giorni</h2>
                <p className="text-sm text-slate-500">La sala si affitta a giornate intere. Tocca il primo giorno e, per più giorni, l'ultimo.</p>
                <DayStrip start={start} setStart={setStart} days={days} busy={avail.busy} room={room} from={f.date_from} to={f.date_to} onSelect={pick} today={today} />
                {f.date_from && (
                  <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="public-day-detail">
                    <div className="text-sm font-semibold text-slate-900" data-testid="public-selected-range">{f.date_from === f.date_to ? `${fmtDate(f.date_from)} · giornata intera` : `Dal ${fmtDate(f.date_from)} al ${fmtDate(f.date_to)} (${sum?.days} giorni)`}</div>
                    {room.cleaning_enabled && <label className="flex items-center gap-3 text-sm"><Checkbox data-testid="public-cleaning" checked={f.cleaning} onCheckedChange={(c) => setF((s) => ({ ...s, cleaning: !!c }))} /> Aggiungi la pulizia della sala ({fmtCHF(room.cleaning_price || 0)})</label>}
                    {sum && (room.price_per_day != null || (f.cleaning && room.cleaning_enabled)) && <div data-testid="public-price-summary" className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900">Totale indicativo: {sum.text}</div>}
                  </div>
                )}
              </section>
            )}
            {room && f.date_from && (
              <section className="space-y-3">
                <h2 className="font-heading text-lg font-semibold text-slate-900">3. I tuoi dati</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Nome *"><Input data-testid="public-first-name" required value={f.first_name} onChange={set("first_name")} className="h-11" /></Field>
                  <Field label="Cognome *"><Input data-testid="public-last-name" required value={f.last_name} onChange={set("last_name")} className="h-11" /></Field>
                  <Field label="E-mail *"><Input data-testid="public-email" type="email" required value={f.email} onChange={set("email")} className="h-11" /></Field>
                  <Field label="Telefono *"><Input data-testid="public-phone" required minLength={6} value={f.phone} onChange={set("phone")} className="h-11" /></Field>
                  <Field label="Tipo di evento *"><Input data-testid="public-event-type" required value={f.event_type} onChange={set("event_type")} className="h-11" placeholder="es. compleanno, riunione" /></Field>
                  <Field label="Partecipanti previsti *"><Input data-testid="public-participants" type="number" min="1" max={room.capacity || undefined} required value={f.participants} onChange={set("participants")} className="h-11" /></Field>
                </div>
                <Field label="Richieste aggiuntive"><Textarea data-testid="public-notes" value={f.notes} onChange={set("notes")} rows={3} /></Field>
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
                {room.rules && (
                  <details className="rounded-lg border bg-white p-3 text-sm"><summary className="cursor-pointer font-semibold" data-testid="public-rules-toggle">Leggi il regolamento della sala (versione {room.rules_version})</summary><p className="mt-2 whitespace-pre-wrap">{room.rules}</p></details>
                )}
                {info.privacy_text && (
                  <details className="rounded-lg border bg-white p-3 text-sm"><summary className="cursor-pointer font-semibold">Leggi l'informativa privacy</summary><p className="mt-2 whitespace-pre-wrap">{info.privacy_text}</p></details>
                )}
                {room.rules && <label className="flex items-start gap-3 text-sm"><Checkbox data-testid="public-accept-rules" checked={f.accept_rules} onCheckedChange={set("accept_rules")} /> Ho letto e accetto il regolamento della sala *</label>}
                <label className="flex items-start gap-3 text-sm"><Checkbox data-testid="public-accept-privacy" checked={f.accept_privacy} onCheckedChange={set("accept_privacy")} /> Acconsento al trattamento dei miei dati per gestire questa richiesta *</label>
                {msg && <p data-testid="public-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{msg}</p>}
                <Button data-testid="public-submit" type="submit" className="w-full h-12 text-base" disabled={busy || !f.accept_privacy || (room.rules && !f.accept_rules)}>{busy ? <Spinner /> : "Invia richiesta"}</Button>
                <p className="text-xs text-slate-500 text-center">La prenotazione è valida solo dopo la conferma dell'associazione.</p>
              </section>
            )}
          </form>
        )}
      </main>
    </div>
  );
}
